// Shared pure-UI primitives used across the Analyze feature

import React from 'react';
import { Box, Typography, Avatar, alpha } from '@mui/material';

// ── Circular SVG progress ring ───────────────────────────────────────────────
export function ScoreRing({ value, max = 100, color, size = 96, label, sub }) {
    const pct = Math.round((value / max) * 100);
    const r = (size - 12) / 2;
    const circ = 2 * Math.PI * r;
    const dash = (pct / 100) * circ;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha(color, 0.12)} strokeWidth={10} />
                    <circle
                        cx={size / 2} cy={size / 2} r={r} fill="none"
                        stroke={color} strokeWidth={10}
                        strokeDasharray={`${dash} ${circ}`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.8s ease' }}
                    />
                </svg>
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, color, lineHeight: 1, fontSize: size > 80 ? '1.2rem' : '1rem' }}
                    >
                        {value}{max !== 100 ? `/${max}` : '%'}
                    </Typography>
                </Box>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.75rem' }}>
                {label}
            </Typography>
            {sub && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', textAlign: 'center' }}>
                    {sub}
                </Typography>
            )}
        </Box>
    );
}

// ── Coloured metric tile ─────────────────────────────────────────────────────
export function MetricTile({ icon, label, value, sub, color }) {
    return (
        <Box
            sx={{
                p: 2.5, borderRadius: 3,
                background: alpha(color, 0.06),
                border: `1px solid ${alpha(color, 0.15)}`,
                display: 'flex', flexDirection: 'column', gap: 0.5,
                transition: 'all 0.2s',
                '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 8px 24px ${alpha(color, 0.2)}`,
                },
            }}
        >
            <Avatar sx={{ bgcolor: alpha(color, 0.14), color, width: 36, height: 36, mb: 0.5 }}>
                {icon}
            </Avatar>
            <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
                {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
                {value}
            </Typography>
            {sub && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                    {sub}
                </Typography>
            )}
        </Box>
    );
}
